"use client"

import * as React from "react"
import { ChevronDown } from "lucide-react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"

// Hook to detect mobile/touch device
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      setIsMobile(isTouchDevice || window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

// Native Select for mobile
const NativeSelectRoot = React.forwardRef<
  HTMLSelectElement,
  {
    value?: string
    onValueChange?: (value: string) => void
    disabled?: boolean
    children: React.ReactNode
    placeholder?: string
    className?: string
  }
>(({ value, onValueChange, disabled, children, placeholder, className }, ref) => {
  return (
    <div className="relative">
      <select
        className={cn(
          "flex h-12 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        value={value}
        onChange={(e) => onValueChange?.(e.target.value)}
        disabled={disabled}
        ref={ref}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  )
})
NativeSelectRoot.displayName = "NativeSelectRoot"

const NativeSelectItem = ({ value, children }: { value: string; children: React.ReactNode }) => (
  <option value={value}>{children}</option>
)

// Radix Select for desktop
const DesktopSelectRoot = SelectPrimitive.Root

const DesktopSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const isRTL = document.documentElement.dir === 'rtl';

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-12 w-full items-center gap-2 rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        isRTL ? "justify-between flex-row-reverse [&>span]:text-right" : "justify-between [&>span]:text-start",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
})
DesktopSelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const DesktopSelectValue = SelectPrimitive.Value

const DesktopSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "item-aligned", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
DesktopSelectContent.displayName = SelectPrimitive.Content.displayName

const DesktopSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 ps-8 pe-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute start-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
DesktopSelectItem.displayName = SelectPrimitive.Item.displayName

// Unified MobileSelect component
export interface MobileSelectProps {
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  children: React.ReactNode
}

export const MobileSelect = React.forwardRef<HTMLSelectElement, MobileSelectProps>(
  ({ value, onValueChange, disabled, placeholder, className, children }, ref) => {
    const isMobile = useIsMobile()

    if (isMobile) {
      return (
        <NativeSelectRoot
          ref={ref as any}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          placeholder={placeholder}
          className={className}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === DesktopSelectItem) {
              return <NativeSelectItem value={child.props.value}>{child.props.children}</NativeSelectItem>
            }
            return child
          })}
        </NativeSelectRoot>
      )
    }

    return (
      <DesktopSelectRoot value={value} onValueChange={onValueChange} disabled={disabled}>
        <DesktopSelectTrigger ref={ref as any} className={className}>
          <DesktopSelectValue placeholder={placeholder} />
        </DesktopSelectTrigger>
        <DesktopSelectContent>
          {children}
        </DesktopSelectContent>
      </DesktopSelectRoot>
    )
  }
)

MobileSelect.displayName = "MobileSelect"

export const MobileSelectItem = DesktopSelectItem
